import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth, API } from "../App";
import { apiFetch } from "../lib/apiFetch";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingPlan = searchParams.get("plan");
  const { user, loading: authLoading, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  // If already logged in, skip the login page entirely and go to the dashboard.
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard/inbox", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const startStripeCheckout = async (planId) => {
    try {
      const originUrl = window.location.origin;
      const r = await apiFetch(`${API}/billing/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: planId, origin_url: originUrl })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) {
        toast.error(d.detail || "Impossible de démarrer le paiement Stripe");
        navigate("/dashboard");
        return;
      }
      window.location.href = d.url;
    } catch (e) {
      toast.error(e.message || "Erreur réseau lors du démarrage Stripe");
      navigate("/dashboard");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiFetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (!response.ok) {
        const detail = data.detail || data.message || (response.status === 401 ? "Email ou mot de passe incorrect" : "Erreur de connexion");
        throw new Error(typeof detail === "string" ? detail : "Erreur de connexion");
      }

      login(data.user, data.access_token);
      toast.success("Connexion réussie !");

      // If arriving from a pricing CTA (?plan=<id>), redirect straight to Stripe
      if (pendingPlan) {
        await startStripeCheckout(pendingPlan);
        return;
      }
      
      if (data.user.onboarding_completed) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch (error) {
      toast.error(error.message || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftLogin = async () => {
    setMsLoading(true);
    try {
      const response = await apiFetch(`${API}/auth/microsoft/login?language=fr`);
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          toast.info("Connectez-vous d'abord à ALIA", {
            description: "Créez un compte ou connectez-vous, puis connectez votre boîte Outlook depuis le tableau de bord.",
            duration: 6000
          });
        } else if (response.status === 503) {
          const errorMsg = data.detail?.message_en || data.detail?.message_fr || "Microsoft not configured";
          toast.error(errorMsg, { duration: 5000 });
        } else {
          toast.error("Erreur lors de la connexion Microsoft");
        }
        setMsLoading(false);
        return;
      }
      
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("URL de connexion Microsoft non disponible");
        setMsLoading(false);
      }
    } catch (error) {
      console.error("Microsoft login error:", error);
      toast.error(error.message || "Erreur de connexion Microsoft");
      setMsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] flex flex-col">
      {/* Back Link */}
      <div className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>
          </div>

          <Card className="glass-card" data-testid="login-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>
                Connexion
              </CardTitle>
              <CardDescription>
                Accédez à votre tableau de bord ALIA
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Google Login */}
              <Button
                type="button"
                variant="outline"
                className="w-full mb-3 py-5"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuer avec Google
              </Button>

              {/* Microsoft Login */}
              <Button
                type="button"
                variant="outline"
                className="w-full mb-4 py-5"
                onClick={handleMicrosoftLogin}
                disabled={msLoading}
                data-testid="microsoft-login-btn"
              >
                {msLoading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                )}
                Continuer avec Microsoft
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-900 px-2 text-slate-500">
                    Ou par email
                  </span>
                </div>
              </div>

              {/* Email Login Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="submit-login-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Connexion...
                    </div>
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-600 dark:text-slate-400 mt-6">
                Pas encore de compte ?{" "}
                <Link to={pendingPlan ? `/register?plan=${encodeURIComponent(pendingPlan)}` : "/register"} className="text-blue-600 hover:underline font-medium">
                  Créer un compte
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
